import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound, Plus, Trash2, Copy, Check } from "lucide-react";
import api, { formatApiError } from "../../api";
import { Badge, Button, Field, Input, Select, Modal, EmptyState } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";

const SA_ROLES = ["admin", "manager", "user", "guest"];
const emptyServiceAccount = { name: "", role: "user" };

/**
 * Service accounts — the API-key principals an external agent (the MCP server)
 * authenticates as.
 *
 * The raw key is shown exactly once, at creation, and is unrecoverable
 * afterwards, which shapes the whole flow: the modal refuses to close silently
 * while a key is on screen, and revoking is a checkbox rather than a delete so
 * `EventLog` attribution survives.
 *
 * No save bar: everything here is an immediate, individually-confirmed action.
 */
export default function ApiKeySettings() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyServiceAccount);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/service-accounts").then((r) => setAccounts(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyServiceAccount);
    setNewKey(null);
    setCopied(false);
    setError("");
    setModal(true);
  };

  const closeModal = () => {
    // Closing while the raw key is still showing discards a secret that cannot
    // be retrieved again, so it takes an explicit confirmation.
    if (newKey && !window.confirm(t("settings.saKeyCloseConfirm"))) return;
    setModal(false);
  };

  const create = async () => {
    setError("");
    try {
      const r = await api.post("/service-accounts", form);
      setNewKey(r.data.api_key);
      load();
    } catch (e) {
      console.error("Service account creation failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toggleActive = async (sa) => {
    if (sa.active && !window.confirm(t("settings.saRevokeConfirm"))) return;
    try {
      await api.patch(`/service-accounts/${sa.id}`, { active: !sa.active });
      load();
    } catch (e) {
      console.error("Service account update failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      await api.delete(`/service-accounts/${id}`);
      load();
    } catch (e) {
      console.error("Service account delete failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <SectionShell
      icon={KeyRound} tone="glow"
      title={t("settings.serviceAccounts")} desc={t("settings.serviceAccountsDesc")}
      error={error}
      aside={
        <Button onClick={openNew} data-testid="add-service-account-btn">
          <Plus size={16} /><span className="hidden sm:inline">{t("settings.newServiceAccount")}</span>
        </Button>
      }>

      <FieldGroups>
        <FieldGroup hint={accounts?.length ? t("settings.saLeastPrivilege") : undefined}>
          {accounts === null ? (
            <p className="text-sm text-muted">{t("common.loading")}</p>
          ) : accounts.length === 0 ? (
            <EmptyState title={t("settings.noServiceAccounts")} hint={t("settings.saEmptyHint")}
              action={<Button onClick={openNew}><Plus size={16} /> {t("settings.newServiceAccount")}</Button>} />
          ) : (
            <ul className="rounded-sm border border-border divide-y divide-border overflow-hidden">
              {accounts.map((sa) => (
                <li key={sa.id} data-testid={`service-account-row-${sa.id}`}
                  className="flex items-center gap-3 px-3.5 py-3 bg-surface hover:bg-bg transition-colors">
                  <span aria-hidden="true"
                    className={`w-8 h-8 rounded-sm shrink-0 flex items-center justify-center ${
                      sa.active ? "bg-glow/15 text-glow" : "bg-surface-soft text-muted"}`}>
                    <KeyRound size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{sa.name}</div>
                    <div className="text-xs text-muted">{t(`roles.${sa.role}`)}</div>
                  </div>
                  <Badge value={sa.active ? "won" : "lost"}
                    label={sa.active ? t("settings.saActive") : t("settings.saRevoked")} />
                  <label className="flex items-center gap-1.5 text-xs text-muted cursor-pointer shrink-0">
                    <input type="checkbox" className="accent-primary cursor-pointer"
                      data-testid={`toggle-service-account-${sa.id}`} checked={sa.active}
                      onChange={() => toggleActive(sa)} />
                    <span className="hidden sm:inline">{t("settings.saActive")}</span>
                  </label>
                  <button onClick={() => remove(sa.id)} data-testid={`delete-service-account-${sa.id}`}
                    title={t("common.delete")} aria-label={t("common.delete")}
                    className="p-1.5 rounded-sm text-muted hover:text-danger hover:bg-danger/10 transition-colors shrink-0">
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </FieldGroup>
      </FieldGroups>

      <Modal open={modal} onClose={closeModal} title={t("settings.newServiceAccount")}
        footer={<>
          <Button variant="ghost" onClick={closeModal}>
            {newKey ? t("common.close") : t("common.cancel")}
          </Button>
          {!newKey && (
            <Button onClick={create} disabled={!form.name.trim()} data-testid="create-service-account-btn">
              {t("common.create")}
            </Button>
          )}
        </>}>
        {newKey ? (
          <div className="space-y-3">
            <p className="text-sm text-danger">{t("settings.saKeyWarning")}</p>
            <div data-testid="new-service-account-key"
              className="bg-bg border border-border rounded-sm p-3 text-xs font-mono break-all select-all">
              {newKey}
            </div>
            <Button variant="ghost" onClick={copyKey} data-testid="copy-service-account-key-btn">
              {copied ? <><Check size={14} /> {t("settings.saCopied")}</> : <><Copy size={14} /> {t("settings.saCopy")}</>}
            </Button>
          </div>
        ) : (
          <>
            <Field label={t("settings.saName")}>
              <Input data-testid="service-account-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label={t("users.role")}>
              <Select data-testid="service-account-role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {SA_ROLES.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
              </Select>
            </Field>
          </>
        )}
      </Modal>
    </SectionShell>
  );
}
